import {
  WaveBlockKind,
  WaveCloudAPI,
  WAVE_APP_ID,
  formatAgentWidgetDisplayText,
  formatWhaleStepLines,
  extractQuestionFromMissionSteps,
  isTerminalAIJobStatus,
  isWhaleExecutionStep,
  resolveWhaleRawDeliverable,
  resolveHandlerFromMissionSteps,
  resolveHandlersFromMissionSteps,
  resolveHandlerFromStreamStep,
  serializeAgentHandlersJson,
  type WaveBlock,
  type WaveDocEditor,
  type WhaleHandlerIdentity,
  type WhaleMissionStep,
} from "@vida/wave-core";
import { streamAgent, type OceanAgentStreamEvent } from "@vida/oceany-agent-client";
import { getIdToken } from "../auth";
import { createOceanSignedFetch } from "./oceanSignedFetch";
import { createWaveApiConfig, trackActivity } from "./waveSession";
import { resolveWhaleJobOutputText } from "./waveAiJobHelpers";
import { downloadBlobText } from "./waveBlobDownload";
import {
  getOceanAgentStreamUrl,
  getWaveWhaleAgentId,
  isWhaleConfigured,
} from "./waveClientConfig";

export const WHALE_STEP_THROTTLE_MS = 1500;
export const WHALE_DEFAULT_ICON = "🐋";
export const WHALE_SESSION_ARCHIVE_CAP = 20;

export type WhaleWidgetAttachment = {
  s3Key: string;
  fileName: string;
  mime: string;
  ragStatus: string;
};

export type WaveWhaleTarget = {
  blockId: string;
  blockKind: WaveBlockKind;
  blockText: string;
  blockLabel: string;
  widgetAttachments?: WhaleWidgetAttachment[];
};

/** @deprecated Use WhaleChatTurn in session storage */
export type WaveWhaleMessage = {
  role: "user" | "assistant";
  content: string;
  agentName?: string;
  agentIcon?: string;
  agentIconS3Key?: string | null;
};

export type WhaleChatAttachment = {
  id: string;
  url: string;
  mime?: string;
};

export type WhaleChatTurn = {
  question: string;
  answer: string;
  agentName: string;
  agentIcon: string;
  agentIconS3Key?: string | null;
  attachments?: WhaleChatAttachment[];
};

export type WhaleChatSessionStatus = "active" | "executed" | "abandoned";

export type WhaleChatSession = {
  id: string;
  startedAt: string;
  endedAt?: string;
  status: WhaleChatSessionStatus;
  jobId?: string;
  handler?: WhaleHandlerIdentity;
  turns: WhaleChatTurn[];
};

export type WhaleWidgetSessions = {
  active: WhaleChatSession | null;
  archive: WhaleChatSession[];
};

export type RunWhaleTaskCallbacks = {
  onMeta?: (meta: {
    jobId: string;
    agentName: string;
    agentIcon: string;
    agentIconS3Key?: string | null;
  }) => void;
  onHandlerChange?: (handler: WhaleHandlerIdentity) => void;
  onExecutionStarted?: (handler: WhaleHandlerIdentity) => void;
  onStep?: (step: WhaleMissionStep) => void;
  onAttachment?: (attachment: { id: string; presignedUrl?: string | null; mime?: string }) => void;
  onDelta?: (text: string) => void;
  onDone?: (result: { outputText: string; handler: WhaleHandlerIdentity; executed: boolean }) => void;
  onError?: (message: string) => void;
};

const SESSION_PREFIX = "wave:whale:";
const SESSIONS_PREFIX = "wave:whale:sessions:";

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function newSessionId(): string {
  return crypto.randomUUID();
}

export function whaleSessionKey(waveId: string, blockId: string): string {
  return `${SESSION_PREFIX}${waveId}#${blockId}`;
}

export function whaleSessionsKey(waveId: string, blockId: string): string {
  return `${SESSIONS_PREFIX}${waveId}#${blockId}`;
}

function emptyWidgetSessions(): WhaleWidgetSessions {
  return { active: null, archive: [] };
}

function flatMessagesToArchivedSession(
  messages: WaveWhaleMessage[],
  status: Exclude<WhaleChatSessionStatus, "active"> = "abandoned",
): WhaleChatSession | null {
  const turns: WhaleChatTurn[] = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role === "user" && messages[i + 1]?.role === "assistant") {
      const assistant = messages[i + 1];
      turns.push({
        question: msg.content,
        answer: assistant.content,
        agentName: assistant.agentName?.trim() || "Whale",
        agentIcon: assistant.agentIcon?.trim() || WHALE_DEFAULT_ICON,
        agentIconS3Key: assistant.agentIconS3Key ?? null,
      });
      i += 1;
    }
  }
  if (!turns.length) return null;
  const now = new Date().toISOString();
  return {
    id: newSessionId(),
    startedAt: now,
    endedAt: now,
    status,
    turns,
  };
}

function migrateFlatHistoryFromStore(
  store: Storage,
  legacyKey: string,
): WhaleWidgetSessions | null {
  const raw = store.getItem(legacyKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as WaveWhaleMessage[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      store.removeItem(legacyKey);
      return null;
    }
    const archived = flatMessagesToArchivedSession(parsed);
    store.removeItem(legacyKey);
    if (!archived) return null;
    return { active: null, archive: [archived] };
  } catch {
    store.removeItem(legacyKey);
    return null;
  }
}

function migrateFlatHistory(waveId: string, blockId: string): WhaleWidgetSessions | null {
  const store = storage();
  if (!store) return null;
  return migrateFlatHistoryFromStore(store, whaleSessionKey(waveId, blockId));
}

function migrateSessionStorageHistory(waveId: string, blockId: string): WhaleWidgetSessions | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return migrateFlatHistoryFromStore(sessionStorage, whaleSessionKey(waveId, blockId));
  } catch {
    return null;
  }
}

export function loadWhaleSessions(waveId: string, blockId: string): WhaleWidgetSessions {
  const store = storage();
  if (!store) return emptyWidgetSessions();
  const key = whaleSessionsKey(waveId, blockId);
  try {
    const raw = store.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as WhaleWidgetSessions;
      if (parsed && Array.isArray(parsed.archive)) {
        return {
          active: parsed.active ?? null,
          archive: parsed.archive,
        };
      }
    }
  } catch {
    /* ignore */
  }
  const migrated = migrateFlatHistory(waveId, blockId) ?? migrateSessionStorageHistory(waveId, blockId);
  if (migrated) {
    saveWhaleSessions(waveId, blockId, migrated);
    return migrated;
  }
  return emptyWidgetSessions();
}

export function saveWhaleSessions(
  waveId: string,
  blockId: string,
  sessions: WhaleWidgetSessions,
): void {
  const store = storage();
  if (!store) return;
  try {
    const archive = sessions.archive.slice(0, WHALE_SESSION_ARCHIVE_CAP);
    store.setItem(
      whaleSessionsKey(waveId, blockId),
      JSON.stringify({ active: sessions.active, archive }),
    );
  } catch {
    /* quota */
  }
}

export function startActiveSessionIfNeeded(
  waveId: string,
  blockId: string,
): WhaleChatSession {
  const sessions = loadWhaleSessions(waveId, blockId);
  if (sessions.active) return sessions.active;
  const active: WhaleChatSession = {
    id: newSessionId(),
    startedAt: new Date().toISOString(),
    status: "active",
    turns: [],
  };
  saveWhaleSessions(waveId, blockId, { ...sessions, active });
  return active;
}

export function appendActiveTurn(
  waveId: string,
  blockId: string,
  turn: WhaleChatTurn,
): WhaleWidgetSessions {
  const sessions = loadWhaleSessions(waveId, blockId);
  const active = sessions.active ?? startActiveSessionIfNeeded(waveId, blockId);
  const next: WhaleWidgetSessions = {
    ...sessions,
    active: {
      ...active,
      turns: [...active.turns, turn],
    },
  };
  saveWhaleSessions(waveId, blockId, next);
  return next;
}

export function clearActiveSession(waveId: string, blockId: string): WhaleWidgetSessions {
  const sessions = loadWhaleSessions(waveId, blockId);
  if (!sessions.active) return sessions;
  const next = { ...sessions, active: null };
  saveWhaleSessions(waveId, blockId, next);
  return next;
}

/** Archives prior Q&A turns (if any) and clears the active slot before mission execution. */
export function stashActiveSessionBeforeExecution(
  waveId: string,
  blockId: string,
  meta: {
    jobId?: string;
    handler?: WhaleHandlerIdentity;
  },
): WhaleWidgetSessions {
  const sessions = loadWhaleSessions(waveId, blockId);
  if (sessions.active?.turns.length) {
    return archiveActiveSession(waveId, blockId, { status: "executed", ...meta });
  }
  return clearActiveSession(waveId, blockId);
}

/** Archives the active chat (if any) and clears the slot for a fresh session. */
export function beginNewWhaleChat(waveId: string, blockId: string): WhaleWidgetSessions {
  const sessions = loadWhaleSessions(waveId, blockId);
  if (sessions.active?.turns.length) {
    return archiveActiveSession(waveId, blockId, { status: "abandoned" });
  }
  return clearActiveSession(waveId, blockId);
}

export function archiveActiveSession(
  waveId: string,
  blockId: string,
  meta: {
    status: Exclude<WhaleChatSessionStatus, "active">;
    jobId?: string;
    handler?: WhaleHandlerIdentity;
  },
): WhaleWidgetSessions {
  const sessions = loadWhaleSessions(waveId, blockId);
  const active = sessions.active;
  if (!active || active.turns.length === 0) {
    const next = { ...sessions, active: null };
    saveWhaleSessions(waveId, blockId, next);
    return next;
  }
  const endedAt = new Date().toISOString();
  const archived: WhaleChatSession = {
    ...active,
    endedAt,
    status: meta.status,
    jobId: meta.jobId,
    handler: meta.handler,
  };
  const next: WhaleWidgetSessions = {
    active: null,
    archive: [archived, ...sessions.archive].slice(0, WHALE_SESSION_ARCHIVE_CAP),
  };
  saveWhaleSessions(waveId, blockId, next);
  return next;
}

/** @deprecated Use loadWhaleSessions */
export function loadWhaleHistory(waveId: string, blockId: string): WaveWhaleMessage[] {
  const sessions = loadWhaleSessions(waveId, blockId);
  const turns = sessions.active?.turns ?? [];
  const history: WaveWhaleMessage[] = [];
  for (const turn of turns) {
    history.push({ role: "user", content: turn.question });
    history.push({
      role: "assistant",
      content: turn.answer,
      agentName: turn.agentName,
      agentIcon: turn.agentIcon,
      agentIconS3Key: turn.agentIconS3Key ?? null,
    });
  }
  return history;
}

/** @deprecated Use session APIs */
export function saveWhaleHistory(
  waveId: string,
  blockId: string,
  history: WaveWhaleMessage[],
): void {
  const turns: WhaleChatTurn[] = [];
  for (let i = 0; i < history.length; i++) {
    const msg = history[i];
    if (msg.role === "user" && history[i + 1]?.role === "assistant") {
      const assistant = history[i + 1];
      turns.push({
        question: msg.content,
        answer: assistant.content,
        agentName: assistant.agentName?.trim() || "Whale",
        agentIcon: assistant.agentIcon?.trim() || WHALE_DEFAULT_ICON,
        agentIconS3Key: assistant.agentIconS3Key ?? null,
      });
      i += 1;
    }
  }
  const sessions = loadWhaleSessions(waveId, blockId);
  saveWhaleSessions(waveId, blockId, {
    ...sessions,
    active: turns.length
      ? {
          id: sessions.active?.id ?? newSessionId(),
          startedAt: sessions.active?.startedAt ?? new Date().toISOString(),
          status: "active",
          turns,
        }
      : sessions.active,
  });
}

function applyTaskHandler(
  editor: WaveDocEditor,
  taskBlockId: string,
  handler: WhaleHandlerIdentity,
): void {
  stampAgentBlockMeta(editor, taskBlockId, {
    authorSub: `agent:${handler.agentId}`,
    authorName: handler.agentName,
    agentIcon: handler.agentIcon,
    agentIconS3Key: handler.agentIconS3Key ?? null,
  });
}

export function whaleTargetLabel(target: WaveWhaleTarget): string {
  return target.blockLabel.trim() || "Widget";
}

export function buildWhaleContextJson(
  waveId: string,
  spaceId: string,
  target: WaveWhaleTarget,
  activeTurns: WhaleChatTurn[] = [],
): Record<string, unknown> {
  return {
    source: "wave",
    spaceId,
    waveId,
    blockId: target.blockId,
    blockKind: target.blockKind,
    blockText: target.blockText,
    blockLabel: target.blockLabel,
    // Scope RAG concessi alla missione: li decide Wave, non la config agente.
    // "space" va aggiunto solo su scelta esplicita dell'utente (toggle futuro).
    ragScopes: ["widget", "thread"],
    widgetAttachments: target.widgetAttachments ?? [],
    chatHistory: activeTurns.map((t) => ({
      user: t.question,
      assistant: t.answer,
    })),
  };
}

export function buildWhaleGoalWithContext(
  goal: string,
  activeTurns: WhaleChatTurn[],
  target: WaveWhaleTarget,
  waveId: string,
): string {
  const trimmed = goal.trim();
  if (!trimmed) return trimmed;
  const parts: string[] = [];
  parts.push("[Wave widget context]");
  parts.push(`waveId: ${waveId}`);
  parts.push(`blockId: ${target.blockId}`);
  parts.push(`blockKind: ${target.blockKind}`);
  parts.push(`blockLabel: ${whaleTargetLabel(target)}`);
  if (target.blockText.trim()) {
    parts.push(`blockText:\n${target.blockText.trim().slice(0, 8000)}`);
  }
  const attachments = target.widgetAttachments ?? [];
  if (attachments.length > 0) {
    const summary = attachments
      .map(
        (a) =>
          `${a.fileName} (${a.mime}, indicizzato: ${a.ragStatus === "indexed" ? "sì" : "no"})`,
      )
      .join("; ");
    parts.push(`attachments: ${attachments.length} file (${summary})`);
  }
  if (activeTurns.length > 0) {
    parts.push("\n[Prior Q&A in this session]");
    for (const turn of activeTurns) {
      parts.push(`User: ${turn.question}`);
      parts.push(`${turn.agentName}: ${turn.answer}`);
    }
  }
  parts.push("\n[Current user request]");
  parts.push(trimmed);
  return parts.join("\n");
}

function streamStepToMissionStep(event: Extract<OceanAgentStreamEvent, { type: "step" }>): WhaleMissionStep {
  const detail = event.detail ? { ...event.detail } : {};
  if (event.peerName) detail.peerName = event.peerName;
  if (event.peerIcon) detail.peerIcon = event.peerIcon;
  if (event.peerIconS3Key) detail.peerIconS3Key = event.peerIconS3Key;
  if (event.peerRole) detail.peerRole = event.peerRole;
  return {
    seq: event.seq,
    kind: event.kind,
    summary: event.summary,
    detailJson: Object.keys(detail).length ? JSON.stringify(detail) : null,
  };
}

export function stampAgentBlockMeta(
  editor: WaveDocEditor,
  blockId: string,
  fields: {
    authorSub: string;
    authorName: string;
    agentIcon?: string;
    agentIconS3Key?: string | null;
    agentStatus?: string;
    agentJobId?: string;
    launchedBy?: string;
    agentQuestion?: string;
  },
): void {
  editor.setMetaString(blockId, "authorSub", fields.authorSub);
  editor.setMetaString(blockId, "authorName", fields.authorName);
  if (fields.agentIcon != null) editor.setMetaString(blockId, "agentIcon", fields.agentIcon);
  if (fields.agentIconS3Key != null) {
    editor.setMetaString(blockId, "agentIconS3Key", fields.agentIconS3Key);
  }
  if (fields.agentStatus != null) editor.setMetaString(blockId, "agentStatus", fields.agentStatus);
  if (fields.agentJobId != null) editor.setMetaString(blockId, "agentJobId", fields.agentJobId);
  if (fields.launchedBy != null) editor.setMetaString(blockId, "launchedBy", fields.launchedBy);
  if (fields.agentQuestion != null) {
    editor.setMetaString(blockId, "agentQuestion", fields.agentQuestion);
  }
}

function stampWhaleAgentTaskBlock(
  editor: WaveDocEditor,
  blockId: string,
  launchedBy: string,
  jobId: string,
  handler: WhaleHandlerIdentity,
  question?: string,
): void {
  const agentQuestion = question?.trim().slice(0, 500);
  stampAgentBlockMeta(editor, blockId, {
    authorSub: `agent:${handler.agentId}`,
    authorName: handler.agentName,
    agentIcon: handler.agentIcon,
    agentIconS3Key: handler.agentIconS3Key ?? null,
    agentStatus: "running",
    agentJobId: jobId,
    launchedBy,
    ...(agentQuestion ? { agentQuestion } : {}),
  });
  editor.setText(blockId, "text", "Task Whale in corso…");
}

export function attachWhaleAgentTask(
  editor: WaveDocEditor,
  parentBlockId: string,
  launchedBy: string,
  jobId: string,
  handler: WhaleHandlerIdentity,
  question?: string,
): string {
  const block = editor.attachBlock(parentBlockId, WaveBlockKind.Text, "agent_task");
  stampWhaleAgentTaskBlock(editor, block.id, launchedBy, jobId, handler, question);
  return block.id;
}

function attachWhaleThreadAgentTask(
  editor: WaveDocEditor,
  launchedBy: string,
  jobId: string,
  handler: WhaleHandlerIdentity,
  question?: string,
): string {
  const block = editor.appendBlock(WaveBlockKind.Text, null, { relation: "agent_task" });
  stampWhaleAgentTaskBlock(editor, block.id, launchedBy, jobId, handler, question);
  return block.id;
}

export function finalizeWhaleAgentResponse(
  editor: WaveDocEditor,
  parentBlockId: string,
  taskBlockId: string,
  outputText: string,
  handler: WhaleHandlerIdentity,
  handlers?: WhaleHandlerIdentity[],
): string {
  void parentBlockId;
  const raw = outputText.trim() || "(Nessun testo di risposta.)";
  const body = formatAgentWidgetDisplayText(raw) || raw;
  const stack = handlers?.length ? handlers : [handler];
  editor.setMetaString(taskBlockId, "agentStatus", "done");
  editor.setMetaString(
    taskBlockId,
    "agentHandlersJson",
    serializeAgentHandlersJson(stack),
  );
  applyTaskHandler(editor, taskBlockId, handler);
  editor.setText(taskBlockId, "text", body);
  return taskBlockId;
}

export type WhaleThreadMissionResult = {
  jobId: string;
  taskBlockId: string;
  agentName: string;
  /** true = missione eseguita (widget agent_task creato); false = solo risposta Q&A. */
  executed: boolean;
  outputText: string;
  handler: WhaleHandlerIdentity;
};

export function buildWhaleThreadContextJson(
  waveId: string,
  spaceId: string,
  preferredAgentId?: string,
): Record<string, unknown> {
  return {
    source: "wave",
    spaceId,
    waveId,
    ragScopes: ["thread"],
    ...(preferredAgentId?.trim() ? { preferredAgentId: preferredAgentId.trim() } : {}),
  };
}

export function buildWhaleThreadGoal(
  goal: string,
  waveId: string,
  mentionLabel?: string,
  mentionAgentName?: string,
  /** L'agente menzionato è invocato direttamente (è lui l'esecutore, non Whale). */
  directInvoke = false,
): string {
  const trimmed = goal.trim();
  const parts = ["[Wave thread chat]", `waveId: ${waveId}`, "", "[Richiesta utente]", trimmed];
  if (directInvoke) {
    // Niente framing da orchestratore: l'agente risponde/esegue come sé stesso.
    parts.push(
      "",
      "[Istruzioni] Se la richiesta è una domanda semplice o informativa, rispondi direttamente " +
        "nel testo, senza usare tool. Usa i tool solo se la richiesta è un task operativo " +
        "(creare, modificare o elaborare contenuti o dati).",
    );
    return parts.join("\n");
  }
  if (mentionLabel && mentionAgentName) {
    parts.push(
      "",
      `[Menzione agente] L'utente si rivolge a @${mentionLabel} (${mentionAgentName}).`,
    );
  }
  parts.push(
    "",
    "[Istruzioni] Se la richiesta è una domanda semplice o informativa, rispondi direttamente " +
      "nel testo, senza delegare e senza usare tool. Solo se la richiesta è un task operativo " +
      "(creare, modificare o elaborare contenuti o dati), esegui la missione" +
      (mentionAgentName ? ` delegando a ${mentionAgentName} se pertinente.` : "."),
  );
  return parts.join("\n");
}

export async function runWhaleThreadMission(input: {
  waveId: string;
  spaceId: string;
  goal: string;
  launchedBy: string;
  editor: WaveDocEditor;
  preferredAgentId?: string;
  mentionLabel?: string;
  mentionAgentName?: string;
  /** Quando set, invoca direttamente questo agente (no orchestratore Whale). */
  invokeAgentId?: string;
}): Promise<WhaleThreadMissionResult> {
  const result = await runWhaleTask({
    waveId: input.waveId,
    spaceId: input.spaceId,
    target: {
      blockId: "",
      blockKind: WaveBlockKind.Text,
      blockText: "",
      blockLabel: "Chat thread",
    },
    goal: input.goal,
    launchedBy: input.launchedBy,
    editor: input.editor,
    threadMission: {
      preferredAgentId: input.preferredAgentId,
      mentionLabel: input.mentionLabel,
      mentionAgentName: input.mentionAgentName,
      invokeAgentId: input.invokeAgentId,
    },
  });
  return {
    jobId: result.jobId,
    taskBlockId: result.taskBlockId,
    agentName: result.handler.agentName,
    executed: result.executed,
    outputText: result.outputText,
    handler: result.handler,
  };
}

export async function runWhaleTask(input: {
  waveId: string;
  spaceId: string;
  target: WaveWhaleTarget;
  goal: string;
  launchedBy: string;
  editor: WaveDocEditor;
  activeTurns?: WhaleChatTurn[];
  callbacks?: RunWhaleTaskCallbacks;
  threadMission?: {
    preferredAgentId?: string;
    mentionLabel?: string;
    mentionAgentName?: string;
    invokeAgentId?: string;
  };
}): Promise<{
  jobId: string;
  taskBlockId: string;
  outputText: string;
  handler: WhaleHandlerIdentity;
  executed: boolean;
}> {
  if (!isWhaleConfigured()) {
    throw new Error("Whale non configurato (client-config).");
  }
  const goal = input.goal.trim();
  if (!goal) throw new Error("Richiesta vuota.");

  const priorTurns = input.activeTurns ?? [];
  const threadMission = input.threadMission;
  const directInvokeId = threadMission?.invokeAgentId?.trim() || "";
  const enrichedGoal = threadMission
    ? buildWhaleThreadGoal(
        goal,
        input.waveId,
        threadMission.mentionLabel,
        threadMission.mentionAgentName,
        Boolean(directInvokeId),
      )
    : buildWhaleGoalWithContext(goal, priorTurns, input.target, input.waveId);

  const streamUrl = getOceanAgentStreamUrl();
  // Menzione di un agente concreto → invocazione diretta (icona/nome deterministici,
  // niente dipendenza dalla delega discrezionale di Whale). Altrimenti orchestratore.
  const agentId = directInvokeId || getWaveWhaleAgentId();
  const jwt = await getIdToken();
  const fetchFn = await createOceanSignedFetch();
  const contextJson = threadMission
    ? buildWhaleThreadContextJson(
        input.waveId,
        input.spaceId,
        threadMission.preferredAgentId,
      )
    : buildWhaleContextJson(input.waveId, input.spaceId, input.target, priorTurns);

  let jobId = "";
  let taskBlockId = "";
  let executionStarted = false;
  let executionCloseNotified = false;
  const steps: WhaleMissionStep[] = [];
  let lastStepFlush = 0;
  let whaleFallback: WhaleHandlerIdentity = {
    agentId,
    agentName: "Whale",
    agentIcon: WHALE_DEFAULT_ICON,
  };
  let liveOutput = "";
  let finalOutput = "";

  const ensureTaskBlock = (handler: WhaleHandlerIdentity) => {
    if (taskBlockId || !jobId) return;
    taskBlockId = threadMission
      ? attachWhaleThreadAgentTask(input.editor, input.launchedBy, jobId, handler, goal)
      : attachWhaleAgentTask(
          input.editor,
          input.target.blockId,
          input.launchedBy,
          jobId,
          handler,
          goal,
        );
  };

  const notifyExecutionStarted = (handler: WhaleHandlerIdentity) => {
    if (executionCloseNotified) return;
    executionCloseNotified = true;
    executionStarted = true;
    ensureTaskBlock(handler);
    input.callbacks?.onExecutionStarted?.(handler);
  };

  const flushSteps = (force = false) => {
    if (!taskBlockId) return;
    if (!force && Date.now() - lastStepFlush < WHALE_STEP_THROTTLE_MS) return;
    lastStepFlush = Date.now();
    input.editor.setText(taskBlockId, "text", formatWhaleStepLines(steps));
  };

  for await (const event of streamAgent({
    streamUrl,
    jwt,
    appId: WAVE_APP_ID,
    agentId,
    goal: enrichedGoal,
    userQuestion: goal,
    contextJson,
    fetchFn,
  })) {
    if (event.type === "meta") {
      jobId = event.jobId;
      whaleFallback = {
        agentId: event.agentId || agentId,
        agentName: event.agentName?.trim() || "Whale",
        agentIcon: event.agentIcon?.trim() || WHALE_DEFAULT_ICON,
        agentIconS3Key: event.agentIconS3Key?.trim() || null,
      };
      input.callbacks?.onMeta?.({
        jobId,
        agentName: whaleFallback.agentName,
        agentIcon: whaleFallback.agentIcon,
        agentIconS3Key: whaleFallback.agentIconS3Key,
      });
      trackActivity("Whale", `Task avviato (${jobId.slice(0, 8)}…)`);
      continue;
    }
    if (event.type === "step") {
      const step = streamStepToMissionStep(event);
      steps.push(step);
      const delegated = resolveHandlerFromStreamStep(step, whaleFallback);
      if (isWhaleExecutionStep(step.kind) && !executionStarted) {
        const execHandler = delegated || whaleFallback;
        whaleFallback = execHandler;
        if (taskBlockId && delegated) {
          applyTaskHandler(input.editor, taskBlockId, delegated);
        }
        notifyExecutionStarted(execHandler);
      } else if (delegated && taskBlockId) {
        whaleFallback = delegated;
        applyTaskHandler(input.editor, taskBlockId, delegated);
        input.callbacks?.onHandlerChange?.(delegated);
      } else if (delegated) {
        whaleFallback = delegated;
        input.callbacks?.onHandlerChange?.(delegated);
      }
      input.callbacks?.onStep?.(step);
      flushSteps(false);
      continue;
    }
    if (event.type === "delta") {
      const channel = event.channel ?? "reasoning";
      liveOutput += event.text;
      if (channel === "final") {
        finalOutput += event.text;
      }
      input.callbacks?.onDelta?.(event.text);
      continue;
    }
    if (event.type === "attachment") {
      input.callbacks?.onAttachment?.({
        id: event.id,
        presignedUrl: event.presignedUrl,
        mime: event.mime,
      });
      continue;
    }
    if (event.type === "error") {
      const msg = event.message || event.code || "Errore stream Whale";
      input.callbacks?.onError?.(msg);
      throw new Error(msg);
    }
    if (event.type === "done") {
      jobId = event.jobId || jobId;
      flushSteps(true);
      const handler = resolveHandlerFromMissionSteps(steps, {
        agentId: whaleFallback.agentId,
        agentName: event.agentName?.trim() || whaleFallback.agentName,
        agentIcon: event.agentIcon?.trim() || whaleFallback.agentIcon,
        agentIconS3Key: event.agentIconS3Key?.trim() || whaleFallback.agentIconS3Key || null,
      });
      const handlers = resolveHandlersFromMissionSteps(steps, handler);
      let resolvedOutput = resolveWhaleRawDeliverable(
        event.outputText,
        finalOutput,
        liveOutput,
        executionStarted,
        steps,
      );
      if (!resolvedOutput && event.outputAssetKey?.trim()) {
        try {
          const fromS3 = await downloadBlobText(event.outputAssetKey.trim());
          if (fromS3.trim()) resolvedOutput = fromS3.trim();
        } catch {
          /* fall through */
        }
      }
      const outputText =
        resolvedOutput ||
        (executionStarted ? "(Missione completata senza testo.)" : "");
      if (executionStarted) {
        if (!executionCloseNotified) {
          notifyExecutionStarted(handler);
        }
        if (!taskBlockId && jobId) {
          ensureTaskBlock(handler);
        }
        if (taskBlockId) {
          finalizeWhaleAgentResponse(
            input.editor,
            input.target.blockId,
            taskBlockId,
            outputText,
            handler,
            handlers,
          );
        }
      }
      input.callbacks?.onHandlerChange?.(handler);
      input.callbacks?.onDone?.({ outputText, handler, executed: executionStarted });
      return { jobId, taskBlockId, outputText, handler, executed: executionStarted };
    }
  }

  if (jobId && (liveOutput.trim() || steps.length > 0)) {
    const handler = resolveHandlerFromMissionSteps(steps, whaleFallback);
    const handlers = resolveHandlersFromMissionSteps(steps, handler);
    let outputText = resolveWhaleRawDeliverable(
      null,
      finalOutput,
      liveOutput,
      executionStarted,
      steps,
    );
    if (!outputText) {
      try {
        const cfg = createWaveApiConfig();
        const job = await WaveCloudAPI.getAIJob(cfg, jobId);
        if (isTerminalAIJobStatus(job.status)) {
          const recovered = await resolveWhaleJobOutputText(job, steps);
          if (recovered) outputText = recovered;
        }
      } catch {
        /* partial stream — keep cascade result */
      }
    }
    if (!outputText && executionStarted) {
      outputText = "(Missione completata senza evento done — output parziale.)";
    }
    if (executionStarted) {
      if (!taskBlockId) {
        ensureTaskBlock(handler);
      }
      if (taskBlockId && outputText) {
        finalizeWhaleAgentResponse(
          input.editor,
          input.target.blockId,
          taskBlockId,
          outputText,
          handler,
          handlers,
        );
      }
      input.callbacks?.onHandlerChange?.(handler);
      input.callbacks?.onDone?.({ outputText, handler, executed: true });
      return { jobId, taskBlockId, outputText, handler, executed: true };
    }
  }

  throw new Error("Stream Whale terminato senza evento done.");
}

export async function reconcileWhaleTask(input: {
  waveId: string;
  parentBlock: WaveBlock;
  taskBlock: WaveBlock;
  editor: WaveDocEditor;
}): Promise<boolean> {
  const jobId = input.taskBlock.payload.agentJobId?.trim();
  if (!jobId) return false;

  const cfg = createWaveApiConfig();
  const job = await WaveCloudAPI.getAIJob(cfg, jobId);
  if (!isTerminalAIJobStatus(job.status)) return false;

  const steps = await WaveCloudAPI.listOceanMissionSteps(cfg, jobId, 80);
  const whaleAgentId = getWaveWhaleAgentId();
  const whaleFallback = {
    agentId: whaleAgentId,
    agentName: "Whale",
    agentIcon: WHALE_DEFAULT_ICON,
  };
  const handler = resolveHandlerFromMissionSteps(steps, whaleFallback);
  const handlers = resolveHandlersFromMissionSteps(steps, handler);

  const recovered =
    (await resolveWhaleJobOutputText(job, steps)) ||
    null;
  const outputText =
    recovered ||
    (job.status === "FAILED"
      ? `Task fallito: ${job.errorMessage || "errore sconosciuto"}`
      : "(Missione completata senza testo.)");

  finalizeWhaleAgentResponse(
    input.editor,
    input.parentBlock.id,
    input.taskBlock.id,
    outputText,
    handler,
    handlers,
  );
  trackActivity("Whale", `Reconciliation job ${jobId.slice(0, 8)}…`);
  return true;
}

const agentQuestionBackfillAttempted = new Set<string>();
const AGENT_QUESTION_BACKFILL_LIMIT = 5;

export function resetWhaleAgentQuestionBackfillAttempts(): void {
  agentQuestionBackfillAttempted.clear();
}

export async function backfillWhaleAgentQuestion(input: {
  editor: WaveDocEditor;
  taskBlock: WaveBlock;
}): Promise<boolean> {
  const blockId = input.taskBlock.id;
  if (agentQuestionBackfillAttempted.has(blockId)) return false;
  agentQuestionBackfillAttempted.add(blockId);

  const jobId = input.taskBlock.payload.agentJobId?.trim();
  if (!jobId) return false;

  const cfg = createWaveApiConfig();
  const steps = await WaveCloudAPI.listOceanMissionSteps(cfg, jobId, 5);
  const question = extractQuestionFromMissionSteps(steps);
  if (!question) return false;

  input.editor.setMetaString(blockId, "agentQuestion", question);
  trackActivity("Whale", `Domanda agente recuperata (${jobId.slice(0, 8)}…)`);
  return true;
}

export { AGENT_QUESTION_BACKFILL_LIMIT };

export function whaleSessionStatusLabel(status: WhaleChatSessionStatus): string {
  switch (status) {
    case "executed":
      return "Eseguito";
    case "abandoned":
      return "Solo Q&A";
    default:
      return "In corso";
  }
}

export function whaleSessionPreview(session: WhaleChatSession): string {
  const first = session.turns[0]?.question.trim();
  if (!first) return "(Nessuna domanda)";
  return first.length > 72 ? `${first.slice(0, 72)}…` : first;
}
